/**
 * Segment menu: user, event and multi cohort segment selection
 */
import flatMap from 'lodash/flatMap';

import {renameProperty} from 'common/report/util';
import {preventTracking, propagateEvent} from 'common/util/dom';
import {pluralize} from 'common/util/index';
import {registerMPElement} from 'common/util/register-element';
import {TimeUnitLabels} from 'common/widgets/date-range-picker/models/types';

import {SavedCohortSegment, CohortSegment, Segment} from './types';
import {PropertiesMenu, PropertyItem} from '../properties-menu/types';
import {ChildMenu} from '../child-menu';
import {Segmentation} from '../../property-filter-menu/models/segfilter';
import {ResourceType} from '../../property-filter-menu/models/types';
import {getCohortFromPropertyItem} from '../util';

import template from './index.jade';

interface SegmentMenuState {
  selected: Segment;
}

export default registerMPElement(
  `mp-segment-menu`,
  class SegmentMenu extends ChildMenu<SegmentMenuState> {
    private propertiesMenu: PropertiesMenu;

    static get attrsSchema() {
      return {
        ...super.attrsSchema,
        'enable-behavioral-properties': {type: `boolean`, default: false},
        'include-custom-properties': {
          type: `string`,
          enum: [`none`, `persisted-only`, `all`],
          default: `none`,
          description: `whether to hide custom properties from properties menus`,
        },
        'other-group-by-options': {
          type: `string`,
          default: `[]`,
          description: `enables breakdown by other keys`,
        },
      };
    }

    get config() {
      return {
        template,

        helpers: {
          ...super.config.helpers,
          preventTracking,
          handleDeleteCustomProperty: (ev) => {
            ev.stopPropagation();
            propagateEvent(this, ev);
          },
          handleSelect: (ev) =>
            this.handleSelect(ev.detail.item, ev.detail.rawItem, ev.detail.item.resourceType, ev.detail.commit),
          handleSelectOption: (ev) => (this.propertiesMenu.useMultipleSelection = ev.detail.selected === `cohort`),
          isCohortSelection: () =>
            this.isOnCohortsTab() || this.getSelectionType(this.attr(`selected`)) === ResourceType.Cohort,
          onMenuInserted: (vnode) => requestAnimationFrame(() => (this.propertiesMenu = vnode.elm)),

          /** Provides a selection value for the underlying menu */
          getMenuSelection: () => JSON.stringify(this.transformSelection(this.attr(`selected`))),

          /** Since mp-properties-menu defines when content is searchable, trigger a manual render */
          handleStagedProperty: () => this.update(),
        },
      };
    }

    constructor() {
      super();

      this.propertiesMenu = null;
    }

    public shouldCloseOnSelect(detail: any) {
      return this.isOnCohortsTab() ? detail.item.length === 0 : true;
    }

    public isContentSearchable() {
      return this.propertiesMenu ? this.propertiesMenu.isContentSearchable() : true;
    }

    public selectedIcon() {
      const {selected} = this.state;
      const selectionType = this.getSelectionType(selected);

      switch (selectionType) {
        case ResourceType.User: {
          const segment = selected[0][selectionType];
          return segment.dataGroupId ? `group-attribute` : `profile-attribute`;
        }
        case ResourceType.Event:
          return `event-attribute`;
        default:
          return null;
      }
    }

    public selectedLabel() {
      // TODO(pablo): use attributes instead of state
      // selected state must be used here since the child menu and
      // query block do not render in the same frame, whenever query block
      // receives new attributes and re-renders, the child menu does not
      // have the updated attributes so label computation is one frame behind
      // updateSync can be used in query-block but there must be a better way
      const {selected} = this.state;
      const selectionType = this.getSelectionType(selected);
      let label = ``;

      // selected labels are based on single segment selections
      switch (selectionType) {
        case ResourceType.Cohort: {
          const length = selected[0].length;
          label = ` by ${length} ${pluralize(`cohort`, length)}`;
          break;
        }
        case ResourceType.User:
        case ResourceType.Event: {
          const selectedProperty = selected[0][selectionType];
          label = ` by ${renameProperty(selectedProperty.propertyName, {
            dataGroupId: selectedProperty.dataGroupId,
            customProperty: selectedProperty.customProperty,
          })}`;
          if (selectedProperty.unit) {
            label = `${label} (${TimeUnitLabels[selectedProperty.unit]})`;
          }
          break;
        }
        case ResourceType.Other:
          label = ` by ${selected[0][selectionType].label}`;
          break;
        default:
          break;
      }

      return `Breakdown${label}`;
    }

    private isOnCohortsTab(): boolean {
      return this.propertiesMenu && this.propertiesMenu.getSelectedTab() === `cohort`;
    }

    private handleSelect(
      item: PropertyItem | PropertyItem[],
      rawItem?: PropertyItem,
      source?: ResourceType,
      commit = false,
    ) {
      const resourceType = this.isOnCohortsTab() ? ResourceType.Cohort : source;
      const selection = this.getSelectionObject(item, resourceType);
      const trackingProps = this.trackingProps(resourceType, selection, rawItem);

      this.dispatchEvent(new CustomEvent(`select`, {detail: {item: selection, trackingProps, rawItem, commit}}));
      this.trackSelection({
        eventName: `Select segment`,
        props: trackingProps,
      });
    }

    /**
     * Generates a Segment for a list of cohort ids or an actual event/user property
     */
    private getSelectionObject(selection: PropertyItem | PropertyItem[], source: ResourceType): Segment[] {
      switch (source) {
        case ResourceType.Cohort: {
          selection = Array.isArray(selection) ? selection : [selection];
          return selection.length ? [selection.map((item) => getCohortFromPropertyItem(item))] : [];
        }
        case ResourceType.User:
          return [{[ResourceType.User]: this.generateEventOrUserSegment(selection, ResourceType.User)}];
        case ResourceType.Event:
          return [{[ResourceType.Event]: this.generateEventOrUserSegment(selection, ResourceType.Event)}];
        case ResourceType.Other:
          return [{[ResourceType.Other]: this.generateOtherSegment(selection)}];
        default:
          throw new Error(`unknown segment type: ${source}`);
      }
    }

    /**
     * Generates a Segment for user or event types
     */
    private generateEventOrUserSegment(item, resourceType): Segmentation {
      const {customProperty, customPropertyId, dataGroupId, propertyDefaultType, type, unit, value} = item;

      return {
        ...(customPropertyId ? {customPropertyId} : null),
        ...(customProperty ? {customProperty} : null),
        ...(dataGroupId ? {dataGroupId} : null),
        resourceType,
        propertyDefaultType,
        propertyName: value,
        propertyType: type,
        ...(unit ? {unit} : {}),
      };
    }

    private generateOtherSegment(item) {
      return {label: item.label, value: item.value, resourceType: item.resourceType};
    }

    /**
     * Infers selection type from the first selected segment
     * TODO: this will change in the future for multi-segment
     */
    private getSelectionType(selection: Segment[]): ResourceType {
      let selectionType = null;
      const hasSelected = Array.isArray(selection) && selection.length && selection[0];

      if (hasSelected) {
        const selectionItem = flatMap(selection)[0];
        selectionType = Object.values(ResourceType).find((resourceType) => resourceType in selectionItem) || null;
      }

      return selectionType;
    }

    /**
     * Transforms the selected format to
     * the format used in mp-properties-menu
     */
    private transformSelection(selection: Segment[]) {
      const selectionType = this.getSelectionType(selection);

      switch (selectionType) {
        case ResourceType.Cohort:
          return flatMap(selection[0], (entry: CohortSegment) => ({
            resourceType: ResourceType.Cohort,
            value:
              (entry as SavedCohortSegment).cohort &&
              ((entry as SavedCohortSegment).cohort.id || {cohort: entry.cohort}), // id does not exist on inline cohorts,
            label: entry.cohort.name,
          }));
        case ResourceType.User:
        case ResourceType.Event: {
          const segment = selection[0][selectionType];
          const propertyType = segment.propertyType || (segment.customProperty && segment.customProperty.propertyType);
          return {
            ...(segment.customPropertyId ? {customPropertyId: segment.customPropertyId} : null),
            ...(segment.customProperty ? {customProperty: segment.customProperty} : null),
            dataGroupId: segment.dataGroupId,
            propertyDefaultType: segment.propertyDefaultType || propertyType,
            resourceType: segment.resourceType,
            type: propertyType,
            value: segment.propertyName,
          };
        }
        case ResourceType.Other: {
          return selection[0][selectionType];
        }
        default:
          return null;
      }
    }

    /**
     * Generates tracking props for the selected segment
     */
    private trackingProps(source: ResourceType, selection: Segment[], rawItem?: PropertyItem) {
      const transformedSelection = this.transformSelection(selection);
      let extraProps = {};
      let segmentName = ``;
      let isCustomPropertySegment = false;

      switch (source) {
        case ResourceType.Cohort:
          segmentName = `cohorts`;
          if (rawItem) {
            extraProps = {
              'cohort visible': !rawItem.hidden,
              'cohort editable': !rawItem.locked,
              'cohort type': rawItem.createdBy
                ? rawItem.createdBy.id === this.attr(`user-id`)
                  ? `cohorts-yours`
                  : `cohorts`
                : `inline cohorts`,
            };
          }
          break;
        case ResourceType.User:
        case ResourceType.Event: {
          const prop = selection[0][source];
          segmentName = prop.propertyName;
          isCustomPropertySegment = !!(prop.customProperty || prop.customPropertyId);
          break;
        }
        case ResourceType.Other:
          segmentName = `Other: ${selection[0][source]}`;
          break;
        default:
          break;
      }

      return {
        ...extraProps,
        'Segment type': source,
        'Segment name': segmentName,
        'Segments length': Array.isArray(transformedSelection) ? transformedSelection.length : 1,
        'Is segment custom property': isCustomPropertySegment,
      };
    }
  },
);
